import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Loader2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { useNotifications } from '@/hooks/useNotifications';
import { NotificationItem } from '@/features/influencer/components/NotificationItem';
import type { PortalRole } from '@/lib/constants';
import type { Notification } from '@socialsoup/shared';

function getNotificationRoute(notification: Notification): string | null {
  const actionUrl = (notification.data as Record<string, unknown> | null)?.actionUrl;
  if (typeof actionUrl !== 'string') return null;
  try {
    const url = new URL(actionUrl);
    return url.pathname;
  } catch {
    if (actionUrl.startsWith('/')) return actionUrl;
    return null;
  }
}

export function NotificationBellDropdown({ portal }: { portal: PortalRole }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { notifications, unreadCount, isLoading, markRead, markAllRead, isMarkingAllRead } =
    useNotifications();

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) {
      void markRead(notification.id);
    }
    const route = getNotificationRoute(notification);
    if (route) {
      setOpen(false);
      navigate(route);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          // carbon-reskin Phase 3 gate (F2). This trigger sits in the dark `<header>` BETWEEN
          // the nav links and the avatar menu, and it was the one control there with no
          // component focus ring — it fell through to `globals.css`'s global black outline over
          // `--ss-black`, 1.20:1, so tabbing across the header went lime ring → nothing → lime
          // ring. Same one-class repair as the hamburger in `TopNavBar.tsx`: #D4FF00 on #1a1a1a
          // is 15.00 (recomputed in this edit, truncated toward zero).
          // Guarded from this file's side too — the ring count in
          // `styles/__tests__/contrast-registry.test.ts` binds here as well as in
          // `TopNavBar.tsx`, because that guard was file-scoped and a ring added
          // outside it would have been unprotected.
          className="relative p-2 rounded-md hover:bg-chrome-bg-hover transition-colors outline-none focus-visible:ring-2 focus-visible:ring-highlight"
          aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'}
        >
          <Bell className="h-4 w-4 text-chrome-text-muted" />
          {unreadCount > 0 && (
            // concept-authority-hardening remediation v2, Phase 5 (CAR-10) —
            // same fix as `TopNavBar.tsx`'s nav badges: was `bg-red-500`
            // (white text at 3.76:1, below 4.5:1 at this 10px size);
            // `bg-status-rejected` is the existing rejected/error token. Its
            // VALUE moved in carbon-reskin Phase 3 (R3) from #B91C1C to
            // Carbon's red-70 #A2191F; white on it is 7.79:1, up from 6.47:1
            // (recomputed in this edit, truncated toward zero).
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-status-rejected px-1 text-[10px] font-semibold text-white">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" sideOffset={8}>
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="text-sm font-semibold text-content-primary">Notifications</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto px-2 py-1 text-xs"
              onClick={() => void markAllRead()}
              disabled={isMarkingAllRead}
            >
              {isMarkingAllRead ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <CheckCheck className="h-3 w-3 mr-1" />
              )}
              Mark all read
            </Button>
          )}
        </div>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-content-muted" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-content-muted">
            <Bell className="h-8 w-8 mb-2 opacity-40" />
            <p className="text-sm font-medium">No notifications</p>
            <p className="text-xs">You're all caught up!</p>
          </div>
        ) : (
          <ScrollArea className="max-h-[400px]">
            <div className="divide-y">
              {notifications.slice(0, 20).map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  portal={portal}
                  onMarkRead={() => handleNotificationClick(notification)}
                />
              ))}
            </div>
          </ScrollArea>
        )}
      </PopoverContent>
    </Popover>
  );
}
